import { z } from 'zod';
import { ResponseError, responseScheme } from '@/models/response';
import { promiseLogger, simpleLogger } from '@/services/sentry';
import { useAuthStore } from '@/stores/auth';
import { fetcher } from '@/utils/fetcher';

export function shouldRefreshToken() {
  const auth = useAuthStore.getState().auth;

  return Boolean(auth?.RefreshToken);
}

export async function refreshToken(signal?: AbortSignal): Promise<string> {
  const auth = useAuthStore.getState().auth;
  if (!auth) {
    simpleLogger({
      name: 'REFRESH_TOKEN',
      requestAttributes: { result: 'unauthenticated', refreshed: false },
    });
    throw new Error('NOT_AUTHENTICATED');
  }
  if (!auth.RefreshToken) {
    useAuthStore.getState().stop();
    simpleLogger({
      name: 'REFRESH_TOKEN',
      requestAttributes: { result: 'no-refresh-token', refreshed: false },
    });
    throw new Error('CANNOT_REFRESH_TOKEN');
  }

  try {
    const response = await promiseLogger({
      name: 'REFRESH_TOKEN',
      action: () =>
        fetcher<RefreshTokenResponse>(
          '/UpdateToken',
          {
            UserID: auth.UserID,
            RefreshToken: auth.RefreshToken,
          },
          {
            target: 'sso',
            requireAuth: false,
            signal,
            schema: refreshTokenResponseSchema,
            validResponseCodes: [0, 1],
          },
        ),
    });

    if (!response.AccessToken) {
      useAuthStore.getState().stop();
      return Promise.reject(new ResponseError(response.Code, response.Msg));
    }

    useAuthStore.getState().updateToken(response.AccessToken);

    return response.AccessToken;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name !== 'CancelledError' && error.name !== 'AbortError') {
        simpleLogger({
          name: 'REFRESH_TOKEN',
          requestAttributes: { result: 'error', refreshed: false, error: JSON.stringify(error) },
        });
        useAuthStore.getState().stop();
      }
    }
    throw error;
  }
}

const refreshTokenResponseSchema = responseScheme.extend({
  AccessToken: z.string().nullish(),
});

type RefreshTokenResponse = z.infer<typeof refreshTokenResponseSchema>;
